import { Box } from '@mui/material';
import { ReactNode } from 'react';
import DataPlusEntitlementModal from './data-plus-entitlement-modal.component';
import { useDataPlusEntitlement } from './use-data-plus-entitlement';

export type DataPlusGateProps = {
  /** Feature label for the paywall copy, e.g. "Retention". */
  featureName: string;
  /**
   * The real (fetching) tab. Only mounted when entitled, so when locked NO data
   * is fetched.
   */
  children: ReactNode;
  /**
   * A non-fetching, fixture-backed render of the same layout, shown blurred
   * behind the modal so the locked state still "looks like" a populated report.
   */
  preview: ReactNode;
  'data-testid'?: string;
};

/**
 * Gates a Data Plus tab. When entitled, renders the real tab. When not, renders
 * the `preview` blurred and inert, with the entitlement modal centered on top —
 * and crucially never mounts `children`, so the locked state issues no requests.
 */
const DataPlusGate = ({
  featureName,
  children,
  preview,
  'data-testid': dataTestId = 'GuestJourney.DataPlus',
}: DataPlusGateProps) => {
  const entitled = useDataPlusEntitlement();

  if (entitled) return <>{children}</>;

  return (
    <Box position="relative" data-testid={`${dataTestId}.Locked`}>
      <Box
        aria-hidden
        data-testid={`${dataTestId}.Preview`}
        sx={{
          filter: 'blur(6px)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {preview}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          pt: 8,
          px: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        }}
      >
        <DataPlusEntitlementModal featureName={featureName} />
      </Box>
    </Box>
  );
};

export default DataPlusGate;
